@if (isEditing) {
  <div class="card-edit-backdrop" aria-hidden="true" appScrollThrough></div>
}
<div class="lead-overview-card" [class.expanded]="isExpanded" [class.editing]="isEditing">
  <!-- Action Icons (Top Right) - Edit/Expand or Cancel/Save -->
  <div class="card-actions">
    @if (!isEditing) {
      <aio-button appearance="neutral-stroke" size="xs" class="edit-button" iconSrc="assets/icons/edit.svg" ariaLabel="Edit" (pressed)="onEditClick()" />
      <button class="action-icon" (click)="onExpandClick()" [class.expanded]="isExpanded" title="Expand">
        <img src="assets/icons/chevron_down.svg" alt="Expand" />
      </button>
    } @else {
      <aio-button appearance="primary" size="sm" [block]="true" class="save-button-changes" (pressed)="onSaveClick()" title="Save Changes">
        Save Changes
      </aio-button>
      <button class="cancel-button-x" (click)="onCancelClick()" title="Cancel">
        <img src="assets/icons/x icon.svg" alt="Cancel" />
      </button>
    }
  </div>

  <!-- Header with title -->
  <div class="overview-header">
    <h3 class="overview-title">Lead Details</h3>
  </div>

  @if (!isEditing) {
    <!-- View Mode: single grid so Type and Status columns align vertically -->
    <div class="info-grid-rows">
      <div class="info-column-item">
        <div class="info-label" appTruncateTooltip>Title</div>
        <div class="info-value">{{ lead?.title || '-' }}</div>
      </div>
      <div class="info-column-item project-type-cell">
        <div class="info-label" appTruncateTooltip>Project Type</div>
        <div class="info-value project-type-list">
          @if (getProjectTypeNameList().length === 0) {
            <span>-</span>
          } @else if (getProjectTypeNameList().length <= 2) {
            @for (name of getProjectTypeNameList(); track name) {
              <div class="project-type-line">{{ name }}</div>
            }
          } @else {
            <div class="project-type-summary">
              <span class="project-type-first">{{ getProjectTypeNameList()[0] }},</span>
              <button type="button" class="project-type-more-btn" (click)="toggleProjectTypesPopover($event)">
                + {{ getProjectTypeNameList().length - 1 }} More
              </button>
            </div>
            @if (showProjectTypesPopover) {
              <div class="project-types-popover" (click)="$event.stopPropagation()">
                <div class="project-types-popover-title">Project Types</div>
                @for (name of getProjectTypeNameList(); track name) {
                  <div class="project-type-popover-line">{{ name }}</div>
                }
              </div>
            }
          }
        </div>
      </div>
      <div class="info-column-item info-column-value-usd">
        <div class="info-label" appTruncateTooltip>Value (USD)</div>
        <div class="info-value">{{ formatBudget(lead?.projectValue) }}</div>
      </div>
      <div class="info-column-item info-column-status">
        <div class="info-label" appTruncateTooltip>Status</div>
        <div class="status-badge"
             [style.background-color]="getTemperatureBadgeColor(lead?.temperatureTypeId)"
             [style.color]="getTemperatureBadgeTextColor(lead?.temperatureTypeId)">
          {{ getTemperatureFromId(lead?.temperatureTypeId) }}
        </div>
      </div>
    </div>

    <!-- Row 3: Owner -->
    <div class="info-row">
      <div class="info-label" appTruncateTooltip>Owner</div>
      <div class="owner-info-horizontal">
        @if (getProjectOwnerProfilePicture()) {
          <img [src]="getProjectOwnerProfilePicture()" alt="Owner" class="owner-avatar" />
        } @else {
          <div class="owner-avatar-placeholder">
            {{ getProjectOwnerName().charAt(0).toUpperCase() }}
          </div>
        }
        <div class="owner-name">{{ getProjectOwnerName() }}</div>
      </div>
    </div>

    <!-- Row 4: Address -->
    <div class="info-row">
      <div class="info-label" appTruncateTooltip>Address</div>
      <a [href]="getGoogleMapsUrl()" target="_blank" class="info-value address-link">{{ getAddress() }}</a>
    </div>

    <!-- Row 5: Referral Source -->
    <div class="info-row">
      <div class="info-label" appTruncateTooltip>Referral Source</div>
      <div class="info-value">{{ getReferralSource() }}</div>
    </div>

    <!-- Row 6: Origin -->
    <div class="info-row">
      <div class="info-label" appTruncateTooltip>Origin</div>
      <div class="info-value">{{ getOrigin() }}</div>
    </div>

    <!-- Row 7: Description Box -->
    <div class="description-box">
      <div class="description-label" appTruncateTooltip>Description</div>
      <div class="description-value">{{ lead?.description || '-' }}</div>
    </div>

    <!-- Row 8: Labels -->
    @if (getLabels().length > 0) {
      <div class="info-row">
        <div class="labels-container">
          @for (label of getLabels(); track label.labelTagId || label.id) {
            @let labelColors = getCustomColor(label);
            <aio-tag
              [label]="getLabelName(label)"
              [textColor]="labelColors.text"
              [backgroundColor]="labelColors.background" />
          }
        </div>
      </div>
    }

    <!-- Custom Fields Section -->
    @if (lead && lead.customAttributes && lead.customAttributes.length > 0) {
      <div class="custom-attributes-section">
        <div class="custom-attributes-header">
          <h4 class="custom-attributes-title">Custom Fields</h4>
        </div>
        <app-custom-attributes-view
          [customAttributes]="lead.customAttributes"
          [attributeDefinitions]="customAttributeDefinitions"
          [twoColumnLayout]="true"
          [isLeadsView]="true">
        </app-custom-attributes-view>
      </div>
    }
  } @else {
    <!-- Edit Mode -->
    <!-- Row 1: Title only -->
    <div class="edit-row-full">
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>Title<span class="required-asterisk">*</span></label>
        <aio-input-field class="edit-input" [fullWidth]="true" [(ngModel)]="editTitle" />
      </div>
    </div>

    <!-- Row 2: Type and Owner -->
    <div class="edit-row">
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>Project Type</label>
        <aio-multi-select
          class="edit-select"
          [(ngModel)]="editProjectTypeIds"
          [options]="projectTypeMultiSelectOptions"
          display="count"
          countSuffix="items selected"
          placeholder="Select types"
          [fullWidth]="true" />
      </div>
      <div class="edit-field owner-select-field">
        <label class="edit-label" appTruncateTooltip>Owner</label>
        <aio-select
          class="edit-select"
          [(ngModel)]="editProjectOwnerId"
          [options]="userOptions"
          [clearable]="true"
          [fullWidth]="true"
          placeholder="Select Owner">
          <ng-template [aioSelectValue]="userOptions" let-option>
            @if (getSelectedOwner()) {
              <div class="owner-chip-inline">
                @if (getSelectedOwner()?.profilePictureUrl) {
                  <img [src]="getSelectedOwner()?.profilePictureUrl" alt="Owner" class="owner-chip-avatar-inline" />
                } @else {
                  <div class="owner-chip-avatar-placeholder-inline">
                    {{ getUserDisplayName(getSelectedOwner()).charAt(0).toUpperCase() }}
                  </div>
                }
                <span class="owner-chip-name-inline">{{ getUserDisplayName(getSelectedOwner()) }}</span>
              </div>
            }
          </ng-template>
        </aio-select>
      </div>
    </div>

    <!-- Row 3: Value and Status -->
    <div class="edit-row">
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>Value (USD)</label>
        <aio-input-field class="edit-input" [fullWidth]="true" [value]="getFormattedBudgetDisplay()" [transform]="formatDollarInput" (valueChange)="onBudgetInput($event)" placeholder="$0.00" />
      </div>
      <div class="edit-field status-select-field">
        <label class="edit-label" appTruncateTooltip>Status</label>
        <aio-select
          class="edit-select status-select"
          [(ngModel)]="editTemperatureTypeId"
          [options]="temperatureDropdownOptions"
          [clearable]="true"
          [fullWidth]="true"
          [placeholder]="statusPlaceholderLabel">
          <ng-template [aioSelectValue]="temperatureDropdownOptions" let-option>
            <div class="owner-chip-inline owner-chip-inline--text-only">
              <span class="owner-chip-name-inline">{{ getTemperatureFromId(option.value) }}</span>
            </div>
          </ng-template>
        </aio-select>
      </div>
    </div>

    <!-- Row 4: Address (plain fields — no Google Places suggestions) -->
    <div class="edit-row-full">
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>Street Address</label>
        <aio-input-field class="edit-input" [fullWidth]="true" [(ngModel)]="editStreet1" autocomplete="street-address" />
      </div>
    </div>
    <div class="edit-row-full">
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>Street Address Line 2</label>
        <aio-input-field class="edit-input" [fullWidth]="true" [(ngModel)]="editStreet2" autocomplete="address-line2" />
      </div>
    </div>
    <div class="edit-row">
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>City</label>
        <aio-input-field class="edit-input" [fullWidth]="true" [(ngModel)]="editCity" autocomplete="address-level2" />
      </div>
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>State</label>
        <aio-input-field class="edit-input" [fullWidth]="true" [(ngModel)]="editState" autocomplete="address-level1" />
      </div>
    </div>
    <div class="edit-row">
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>Zip Code</label>
        <aio-input-field class="edit-input" [fullWidth]="true" [(ngModel)]="editZip" autocomplete="postal-code" />
      </div>
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>Country</label>
        <aio-input-field class="edit-input" [fullWidth]="true" [(ngModel)]="editCountry" autocomplete="country-name" />
      </div>
    </div>

    <!-- Row 5: Origin and Referral Source -->
    <div class="edit-row">
      <div class="edit-field origin-select-field">
        <label class="edit-label" appTruncateTooltip>Origin</label>
        <aio-select
          class="edit-select"
          [(ngModel)]="editLeadOriginId"
          [options]="leadOriginOptions"
          [clearable]="true"
          [fullWidth]="true"
          [placeholder]="originPlaceholderLabel" />
      </div>
      <div class="edit-field referral-select-field">
        <label class="edit-label" appTruncateTooltip>Referral Source</label>
        <aio-select
          class="edit-select"
          [(ngModel)]="editReferralChannelId"
          [options]="referralChannelOptions"
          [clearable]="true"
          [fullWidth]="true"
          [placeholder]="referralPlaceholderLabel" />
      </div>
    </div>

    <!-- Row 6: Description -->
    <div class="edit-row-full">
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>Description</label>
        <aio-input-field class="edit-textarea" [fullWidth]="true" [multiline]="true" [rows]="4" [(ngModel)]="editDescription" />
      </div>
    </div>

    <!-- Row 7: Labels -->
    <div class="edit-row-full">
      <div class="edit-field">
        <label class="edit-label" appTruncateTooltip>Labels</label>
        <app-multi-select-dropdown
          [(ngModel)]="editLabelIds"
          [options]="labelSelectOptions"
          [width]="'100%'"
          controlSize="add"
          [maxSelectedLabels]="999"
          [showClear]="false"
          placeholder="Select labels"
          class="label-select">
        </app-multi-select-dropdown>
      </div>
    </div>

    <!-- Custom Fields Section -->
    <div class="custom-attributes-section">
      <div class="custom-attributes-header">
        <h4 class="custom-attributes-title">Custom Fields</h4>
      </div>
      @if (customAttributeDefinitions && customAttributeDefinitions.length > 0) {
        <app-custom-attributes-form
          [definitions]="customAttributeDefinitions"
          [existingAttributes]="lead?.customAttributes || []"
          [entityId]="lead?.id || 0"
          [entityType]="'LEAD'"
          controlSize="detail">
        </app-custom-attributes-form>
      }
    </div>
  }
</div>
